import React, { useEffect, useState } from 'react';
import { getUsers, deleteUser, getTeams, createTeam, deleteTeam } from '../api/client';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import { C, Btn, Input, Toast, Spinner, initials, useNotify } from '../utils/styles';

const ROLE_COLORS = { superadmin:C.primary, admin:C.purple, member:C.green, viewer:C.muted };

// ── Modal shell — overlay + centered card ────────────────────
const Modal = ({ title, icon, onClose, children, width = 480 }) => (
  <div
    onClick={onClose}
    style={{
      position:'fixed', inset:0, background:'rgba(15,15,15,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:1000, padding:20,
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        width, maxWidth:'100%', maxHeight:'88vh', overflowY:'auto',
        background:'#fff', borderRadius:16, padding:24,
        boxShadow:'0 25px 60px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
        <div style={{ fontWeight:600, fontSize:15, color:C.dark, display:'flex', alignItems:'center', gap:8 }}>
          <i className={`ti ${icon}`} style={{ color:C.primary, fontSize:18 }} />{title}
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, fontSize:18, padding:4, lineHeight:1 }}>
          <i className="ti ti-x" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export default function Team() {
  const { isSuperAdmin, isAdmin } = useAuth();
  const [users,       setUsers]      = useState([]);
  const [teams,       setTeams]      = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [activeTab,   setActiveTab]  = useState('users');
  const [showInvite,  setShowInvite] = useState(false);
  const [showAddTeam, setShowAddTeam]= useState(false);
  const [inviteSent,  setInviteSent] = useState(false);
  const [editUser,    setEditUser]   = useState(null);
  const [resending,   setResending]  = useState(null);
  const [notification, notify]       = useNotify();

  const [invite,    setInvite]    = useState({ name:'', email:'', role:'member', team_id:'' });
  const [newTeam,   setNewTeam]   = useState({ name:'', description:'' });

  // ── Admin password override (inside Edit modal) ────────────
  const [newPassword,    setNewPassword]    = useState('');
  const [confirmPassword,setConfirmPassword]= useState('');
  const [settingPassword,setSettingPassword]= useState(false);
  const [passwordSetMsg, setPasswordSetMsg] = useState('');

  const fetchAll = async () => {
    try {
      const [u, t] = await Promise.all([getUsers(), getTeams()]);
      setUsers(u.data||[]);
      setTeams(t.data||[]);
    } catch (err) { notify(err.response?.data?.error||'Failed to load','error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const closeInviteModal = () => {
    setShowInvite(false);
    setInviteSent(false);
    setInvite({ name:'', email:'', role:'member', team_id:'' });
  };

  const handleSendInvite = async () => {
    if (!invite.email) return notify('Email is required','error');
    try {
      await client.post('/api/users/invite', invite);
      setInviteSent(true);
      notify(`Invite sent to ${invite.email}!`);
      fetchAll();
    } catch (err) { notify(err.response?.data?.error||'Failed to send invite','error'); }
  };

  const handleResendInvite = async (email) => {
    setResending(email);
    try {
      await client.post('/api/users/resend-invite', { email });
      notify(`Invite resent to ${email}!`);
    } catch (err) { notify(err.response?.data?.error||'Failed to resend','error'); }
    finally { setResending(null); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Remove this user?')) return;
    try { await deleteUser(id); notify('User removed'); fetchAll(); }
    catch (err) { notify(err.response?.data?.error||'Failed to remove','error'); }
  };

  const openEditModal = (u) => {
    setEditUser({ ...u, team_id: u.team_id || '' });
    setNewPassword('');
    setConfirmPassword('');
    setPasswordSetMsg('');
  };

  const closeEditModal = () => {
    setEditUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordSetMsg('');
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    try {
      await client.patch(`/api/users/${editUser.id}`, {
        name:    editUser.name,
        role:    editUser.role,
        team_id: editUser.team_id || null,
        status:  editUser.status,
      });
      notify('User updated!');
      closeEditModal();
      fetchAll();
    } catch (err) { notify(err.response?.data?.error||'Failed to update','error'); }
  };

  // ── Admin password override — for invites that never arrived ──
  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setPasswordSetMsg('error:Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordSetMsg('error:Passwords do not match');
      return;
    }
    setSettingPassword(true);
    setPasswordSetMsg('');
    try {
      const { data } = await client.patch(`/api/users/${editUser.id}/set-password`, { password: newPassword });
      setPasswordSetMsg(`success:${data.message || 'Password set successfully.'}`);
      setNewPassword('');
      setConfirmPassword('');
      fetchAll();
    } catch (err) {
      setPasswordSetMsg(`error:${err.response?.data?.error || 'Failed to set password'}`);
    } finally {
      setSettingPassword(false);
    }
  };

  const handleAddTeam = async () => {
    if (!newTeam.name) return notify('Team name required','error');
    try {
      await createTeam(newTeam);
      notify('Team created!');
      setShowAddTeam(false);
      setNewTeam({ name:'', description:'' });
      fetchAll();
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const handleDeleteTeam = async (id) => {
    if (!window.confirm('Delete this team?')) return;
    try { await deleteTeam(id); notify('Team deleted'); fetchAll(); }
    catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const canManage = (targetRole) => {
    if (targetRole==='superadmin') return false;
    if (isSuperAdmin) return true;
    if (isAdmin && targetRole==='member') return true;
    return false;
  };

  const roleOptions = isSuperAdmin ? ['admin','member'] : ['member'];

  if (loading) return <Spinner />;

  const TABS = [
    { id:'users', label:'Users',  icon:'ti-users',       count:users.length  },
    { id:'teams', label:'Teams',  icon:'ti-users-group', count:teams.length  },
  ];

  const inputStyle = { width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none' };
  const labelStyle = { fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' };

  return (
    <div>
      <Toast {...notification} />

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:C.dark, marginBottom:2 }}>Team & Users</h1>
          <p style={{ fontSize:13, color:C.muted }}>{users.length} users · {teams.length} teams</p>
        </div>
        {isAdmin && (
          <Btn onClick={() => setShowInvite(true)}>
            <i className="ti ti-user-plus" /> Invite User
          </Btn>
        )}
      </div>

      {/* ── Invite User Modal ── */}
      {showInvite && (
        <Modal title="Invite User" icon="ti-mail-forward" onClose={closeInviteModal}>
          {inviteSent ? (
            <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:16, textAlign:'center' }}>
              <i className="ti ti-circle-check" style={{ fontSize:28, color:C.success, display:'block', marginBottom:8 }} />
              <div style={{ fontWeight:600, fontSize:14, color:'#166534', marginBottom:4 }}>Invite sent to {invite.email}!</div>
              <div style={{ fontSize:12, color:'#166534', marginBottom:14 }}>
                They'll receive an email with a link to complete registration. They'll stay <strong>pending</strong> until they finish setting up their account.
              </div>
              <Btn onClick={closeInviteModal}>Done</Btn>
            </div>
          ) : (
            <>
              <p style={{ fontSize:12, color:C.muted, marginBottom:16 }}>
                An invitation email will be sent. The user completes their own registration and sets their own password.
              </p>
              <div style={{ display:'grid', gap:12, marginBottom:16 }}>
                <Input label="Full Name (optional)" placeholder="John Doe" value={invite.name} onChange={e => setInvite({...invite,name:e.target.value})} />
                <Input label="Email Address" type="email" placeholder="user@example.com" value={invite.email} onChange={e => setInvite({...invite,email:e.target.value})} />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={labelStyle}>Role</label>
                    <select style={inputStyle} value={invite.role} onChange={e => setInvite({...invite,role:e.target.value})}>
                      {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Team</label>
                    <select style={inputStyle} value={invite.team_id} onChange={e => setInvite({...invite,team_id:e.target.value})}>
                      <option value="">No team</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <Btn onClick={handleSendInvite}><i className="ti ti-send" /> Send Invite</Btn>
                <Btn variant="outline" onClick={closeInviteModal}>Cancel</Btn>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Edit User Modal ── */}
      {editUser && (
        <Modal title={`Edit — ${editUser.name}`} icon="ti-edit" onClose={closeEditModal}>
          <div style={{ display:'grid', gap:12, marginBottom:18 }}>
            <Input label="Full Name" value={editUser.name} onChange={e => setEditUser({...editUser,name:e.target.value})} />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={labelStyle}>Role</label>
                <select style={inputStyle} value={editUser.role} onChange={e => setEditUser({...editUser,role:e.target.value})}>
                  {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Team</label>
                <select style={inputStyle} value={editUser.team_id||''} onChange={e => setEditUser({...editUser,team_id:e.target.value})}>
                  <option value="">No team</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={editUser.status||'active'} onChange={e => setEditUser({...editUser,status:e.target.value})}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:20 }}>
            <Btn onClick={handleEditUser}><i className="ti ti-device-floppy" /> Save Changes</Btn>
            <Btn variant="outline" onClick={closeEditModal}>Cancel</Btn>
          </div>

          {/* ── Admin password override ── */}
          <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:18 }}>
            <div style={{ fontWeight:600, fontSize:13, color:C.dark, marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
              <i className="ti ti-key" style={{ color:C.primary }} /> Set Password Directly
            </div>
            <p style={{ fontSize:12, color:C.muted, marginBottom:12 }}>
              Use this if {editUser.name} never received their invite email. This activates their account immediately so they can log in.
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:10 }}>
              <Input label="New Password" type="password" placeholder="Min 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <Input label="Confirm Password" type="password" placeholder="Re-enter password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            </div>
            {passwordSetMsg && (
              <div style={{
                fontSize:12, marginBottom:10, padding:'8px 12px', borderRadius:8,
                background: passwordSetMsg.startsWith('error:') ? '#fef2f2' : '#f0fdf4',
                color:      passwordSetMsg.startsWith('error:') ? '#991b1b' : '#166534',
              }}>
                {passwordSetMsg.split(':').slice(1).join(':')}
              </div>
            )}
            <Btn variant="outline" onClick={handleSetPassword} disabled={settingPassword}>
              <i className={`ti ${settingPassword?'ti-loader ti-spin':'ti-key'}`} />
              {settingPassword ? 'Setting...' : 'Set Password & Activate'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:6 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 12px', borderRadius:8, border:'none', background:activeTab===t.id?C.primary:'transparent', color:activeTab===t.id?'#fff':C.muted, fontSize:13, fontWeight:activeTab===t.id?600:400, cursor:'pointer' }}>
            <i className={`ti ${t.icon}`} style={{ fontSize:15 }} />{t.label}
            <span style={{ background:activeTab===t.id?'rgba(255,255,255,0.25)':'var(--color-primary-light)', color:activeTab===t.id?'#fff':C.primary, borderRadius:20, padding:'1px 8px', fontSize:11 }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Users list */}
      {activeTab==='users' && (
        <div>
          <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden' }}>
            {users.map((u, i) => (
              <div key={u.id} style={{ borderBottom:i<users.length-1?'1px solid #f3f4f6':'none' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px' }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', background:ROLE_COLORS[u.role]||C.primary, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:700, flexShrink:0 }}>
                    {initials(u.name)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:600, fontSize:13 }}>{u.name}</span>
                      <span style={{ background:`${ROLE_COLORS[u.role]||C.primary}18`, color:ROLE_COLORS[u.role]||C.primary, borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:500 }}>{u.role}</span>
                      {u.role==='superadmin' && <i className="ti ti-shield-check" style={{ fontSize:14, color:C.primary }} />}
                    </div>
                    <div style={{ fontSize:12, color:C.muted }}>
                      {u.email}
                      {u.teams?.name && <span> · <i className="ti ti-users-group" style={{ fontSize:11 }} /> {u.teams.name}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:11, color:u.status==='active'?C.success:u.status==='invited'?C.warning:C.danger, background:u.status==='active'?'#f0fdf4':u.status==='invited'?'#fffbeb':'#fef2f2', borderRadius:20, padding:'2px 8px', flexShrink:0 }}>
                      {u.status}
                    </span>
                    {canManage(u.role) && (u.status==='invited'||u.status==='inactive') && (
                      <button
                        onClick={() => handleResendInvite(u.email)}
                        disabled={resending===u.email}
                        title="Resend invite"
                        style={{ padding:'5px 10px', borderRadius:6, border:`1px solid ${C.primary}`, background:'var(--color-primary-light)', color:C.primary, cursor:'pointer', fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
                        <i className={`ti ${resending===u.email?'ti-loader ti-spin':'ti-mail-forward'}`} style={{ fontSize:13 }} />
                        {resending===u.email?'Sending...':'Resend'}
                      </button>
                    )}
                    {canManage(u.role) && (
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => openEditModal(u)}
                          style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'var(--color-primary-light)', color:C.primary, cursor:'pointer', fontSize:13 }}>
                          <i className="ti ti-edit" />
                        </button>
                        <button onClick={() => handleDeleteUser(u.id)}
                          style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'#FEF2F2', color:C.danger, cursor:'pointer', fontSize:13 }}>
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!users.length && <div style={{ padding:30, textAlign:'center', color:C.faint, fontSize:13 }}>No users yet.</div>}
          </div>
        </div>
      )}

      {/* Teams list */}
      {activeTab==='teams' && (
        <div>
          {isSuperAdmin && (
            <div style={{ marginBottom:12, display:'flex', justifyContent:'flex-end' }}>
              <Btn onClick={() => setShowAddTeam(!showAddTeam)}><i className="ti ti-plus" /> New Team</Btn>
            </div>
          )}
          {showAddTeam && (
            <div style={{ background:'#f9fafb', borderRadius:12, border:`1px solid ${C.border}`, padding:16, marginBottom:12 }}>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>New Team</div>
              <div style={{ display:'grid', gap:10, marginBottom:12 }}>
                <Input label="Team Name"   placeholder="e.g. Design"             value={newTeam.name}        onChange={e => setNewTeam({...newTeam,name:e.target.value})} />
                <Input label="Description" placeholder="What does this team do?" value={newTeam.description} onChange={e => setNewTeam({...newTeam,description:e.target.value})} />
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <Btn onClick={handleAddTeam}><i className="ti ti-check" /> Create</Btn>
                <Btn variant="outline" onClick={() => setShowAddTeam(false)}>Cancel</Btn>
              </div>
            </div>
          )}
          <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden' }}>
            {teams.map((t, i) => (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', borderBottom:i<teams.length-1?'1px solid #f3f4f6':'none' }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'var(--color-primary-light)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <i className="ti ti-users-group" style={{ fontSize:20, color:C.primary }} />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:2 }}>{t.name}</div>
                  <div style={{ fontSize:12, color:C.muted }}>{t.description}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ background:'var(--color-primary-light)', color:C.primary, borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:500 }}>
                    {users.filter(u => u.team_id===t.id).length} members
                  </span>
                  {isSuperAdmin && (
                    <button onClick={() => handleDeleteTeam(t.id)} style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'#FEF2F2', color:C.danger, cursor:'pointer', fontSize:13 }}>
                      <i className="ti ti-trash" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!teams.length && <div style={{ padding:30, textAlign:'center', color:C.faint, fontSize:13 }}>No teams yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
